import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'

import { useDialog } from '_hooks'
import Storage from '@st-operations/uikit/Storage'

// UIKIT dependencies
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  Button,
  Icon,
  Input,
  INPUT_THEME,
} from '@st-operations/uikit/src/v2/index.js'

import './styles.scss'

const SEARCH_MODIFIERS = {
  TYPE: [
    { name: 'None', value: 'none' },
    { name: 'Similar', value: 'similar' },
    { name: 'Popular', value: 'most popular' },
  ],
  KECC: [
    { name: 'KECC', value: 'kecc' },
    { name: 'Keywords', value: 'keywords' },
  ],
  KEYWORD_EXPANSION: [
    { name: 'v1', value: 'v1' },
    { name: 'v2', value: 'v2' },
  ],
  ACCURACY: [
    { name: 'Precise', value: 1 },
    { name: 'Balanced', value: 3 },
    { name: 'Reach', value: 5 },
  ],
}

const HorizontalSelect = ({ label, options, value, onChange }) => {
  return (
    <div className="horizontal-select">
      <p className="horizontal-select__label">{label}</p>
      <div className="horizontal-select__options">
        {options.map((option) => (
          <button
            key={option.value}
            className={classNames('horizontal-select__option', {
              'horizontal-select__option--active': value === option.value,
            })}
            onClick={() => onChange(option)}
          >
            {option.name}
          </button>
        ))}
      </div>
    </div>
  )
}

const TopicsInput = ({
  handleSearchTopicsInputChange,
  handleTopicsKeyDown,
  topicsSearch,
  handleScaleChange,
  scale,
  audience,
  handleInputChange,
}) => {
  const baseRef = useRef(null)
  const targetRef = useRef(null)

  const { dialog, Dialog } = useDialog({
    wrapperNode: baseRef.current,
    targetNode: targetRef.current,
    maxSize: {
      width: 600,
      height: 140,
    },
  })

  // Initialize from local storage if audience doesn't have preferences yet
  useEffect(() => {
    const storedType = Storage.get('ac_topic_search_type')
    const storedResource = Storage.get('ac_topic_search_resource')

    const typeValue =
      audience.topicSearchModifier ||
      storedType ||
      SEARCH_MODIFIERS.TYPE[1].value /* default Similar */

    const resourceValue =
      audience.keccType || storedResource || SEARCH_MODIFIERS.KECC[0].value /* default KECC */

    if (audience.topicSearchModifier !== typeValue) {
      handleInputChange(typeValue, 'topicSearchModifier')
    }
    if (audience.keccType !== resourceValue) {
      handleInputChange(resourceValue, 'keccType')
    }
  }, [])

  return (
    <div className="ac-signals-block__topics-input">
      <Input
        name="topics"
        placeholder="Search for topic"
        onChange={handleSearchTopicsInputChange}
        onKeyDown={handleTopicsKeyDown}
        value={topicsSearch}
        theme={INPUT_THEME.SEARCH}
      />
      <div className="ac-signals-block__ai-expansion" ref={baseRef}>
        <Button
          size={BUTTON_SIZE.SMALL}
          title="AI Expansion"
          theme={BUTTON_THEME.GHOST}
          ref={targetRef}
          onClick={dialog.show}
        >
          <Icon width="22px" height="22px" icon="settings" type="outline" color="currentColor" />
        </Button>
        <Dialog>
          <div className="ac-signals-block__ai-expansion-controls">
            <HorizontalSelect
              label="Type"
              options={SEARCH_MODIFIERS.TYPE}
              value={
                audience.topicSearchModifier ||
                Storage.get('ac_topic_search_type') ||
                SEARCH_MODIFIERS.TYPE[1].value
              }
              onChange={(modifier) => {
                Storage.set('ac_topic_search_type', modifier.value)
                handleInputChange(modifier.value, 'topicSearchModifier')
              }}
            />
            <HorizontalSelect
              label="Resource"
              options={SEARCH_MODIFIERS.KECC}
              value={
                audience.keccType ||
                Storage.get('ac_topic_search_resource') ||
                SEARCH_MODIFIERS.KECC[0].value
              }
              onChange={(keccType) => {
                Storage.set('ac_topic_search_resource', keccType.value)
                handleInputChange(keccType.value, 'keccType')
              }}
            />
            <HorizontalSelect
              label="Accuracy"
              options={SEARCH_MODIFIERS.ACCURACY}
              value={SEARCH_MODIFIERS.ACCURACY.find((option) => option.value === scale)?.value}
              onChange={(newScale) => handleScaleChange(newScale?.value)}
            />
          </div>
          <p className="ac-signals-block__ai-expansion-description">
            This will affect how the new topics will be added to the audience. It does not change
            previously added topics.
          </p>
        </Dialog>
      </div>
    </div>
  )
}

export default TopicsInput
